import React, { useState, useEffect, useCallback, useRef } from 'react';
import './WOE.css';
import { getCurrentUser } from '../../utils/auth';
import { isIsraelUser } from '../../utils/locationUtils';
import { API_URL  } from '../../config/api';
import { WIG_OPTIONS, DEFAULT_WIG_VALUES, getColorOptions, formatColorName } from '../../constants/wigOptions';
import { inventoryService } from '../../services/inventoryService';
import { normalizeOrderType } from '../../utils/orderTypeUtils';
import { apiService } from '../../services/apiService';

import AutoCompleteButton from '../buttons/AutoCompleteButton';



console.log('Using API URL:', API_URL);

const WigDetails = ({ customerInfo, savedWigData, draftBarcodeNote = null, draftServicePrice = null, draftWarrantyInfo = null, onPrevious, onNext, onStateChange }) => {
  // State for form data
  const [wigData, setWigData] = useState(
    savedWigData?.wigData || {
      wigType: '',
      hairLength: '',
      cutStyle: '',
      baseColor: '',
      inventoryType: '',
      netSize: '',
      hairType: '',
      netType: '',
      laceType: '',
      barcode: '',
      serialCode: '',
      virtualConsult: 'No Virtual Consult',
    }
  );
  const orderHistoryRefreshRef = useRef(null);
  const [showAddToInventory, setShowAddToInventory] = useState(false);
  const [isAddingToInventory, setIsAddingToInventory] = useState(false);
  const [addInventorySuccess, setAddInventorySuccess] = useState(false);
  const [accessorySearchTerm, setAccessorySearchTerm] = useState('');
  

const refreshOrderHistory = useCallback(async () => {
  if (!customerInfo?.ID) {
    setOrderHistory([]);
    return;
  }

  try {
    const response = await apiService.get(`/api/customers/${customerInfo.ID}/orders`);

    const formattedOrders = (response.orders || []).map(order => ({
      orderNo: order.orderno,
      barCode: order.Wig_Bar_Code,
      serialCode: order.Wig_Serial_Code,
      orderStatus: order.OrderStatus || 'Unknown',
      orderDate: order.OrderDate,
      dueDate: order.DueDate || 'N/A',
      // Warranty is resolved per wig barcode by the API (Wig_Warranty_State is
      // 'active' | 'expired' | 'none'); the dates belong to the wig, not this row.
      warrantyStartDate: order.Wig_Warranty_Start_Date || null,
      warrantyExpiresDate: order.Wig_Warranty_Expires_Date || null,
      warrantyState: order.Wig_Warranty_State || 'none',
      warrantySourceOrderNo: order.Wig_Warranty_Source_OrderNo || null,
      customerName: order.OrderCustomerName,
      customerNo: order.OrderCustomerNo,
      orderType: order.OrderWigType
    }));
    
    setOrderHistory(formattedOrders);
    console.log("✅ Order history refreshed successfully");
  } catch (error) {
    console.error("❌ Error refreshing order history:", error);
    setOrderHistory([]);
  }
}, [customerInfo?.ID]);
  orderHistoryRefreshRef.current = refreshOrderHistory;

  useEffect(() => {
    const handleOrderAutoCompleted = (event) => {
      if (refreshOrderHistory) {
        refreshOrderHistory();
      }
    };

    window.addEventListener('orderAutoCompleted', handleOrderAutoCompleted);
    return () => window.removeEventListener('orderAutoCompleted', handleOrderAutoCompleted);
  }, [refreshOrderHistory]);

  //State for price editing
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editablePricingData, setEditablePricingData] = useState([]);

  // For displaying customer orders history when needed
  const [showOrderHistory] = useState(true);
  
  // State for pricing display
  const [showPricing, setShowPricing] = useState(false);
  const [repairSearchTerm, setRepairSearchTerm] = useState('');
  
  const [selectedItems, setSelectedItems] = useState(savedWigData?.selectedItems || []);
  const [selectedAccessories, setSelectedAccessories] = useState(savedWigData?.selectedAccessories || []);
  const [orderHistory, setOrderHistory] = useState([]);

  // Warranty for the currently selected wig, resolved by barcode. Used to gate
  // $0 Wig Repair orders (see repairGate below).
  const [warrantyCheck, setWarrantyCheck] = useState(null);

  const [wigRequirementMet, setWigRequirementMet] = useState(false);
  const checkWigRequirement = useCallback(() => {
    // Order types that don't require a wig
    const noWigRequiredTypes = ['Accessory', 'Consultation'];
    
    // If order type doesn't require a wig, requirement is automatically met
    if (noWigRequiredTypes.includes(customerInfo?.orderType)) {
      return true;
    }
    
    return selectedItems && selectedItems.length > 0;
  }, [customerInfo?.orderType, selectedItems]);


  // 4. Add function to get requirement text for display
  const getWigRequirementText = useCallback(() => {
    const noWigRequiredTypes = ['Accessory', 'Consultation'];
    
    if (noWigRequiredTypes.includes(customerInfo?.orderType)) {
      return null; // No requirement message needed
    }
    
    if (!wigRequirementMet) {
      const actionText = normalizeOrderType(customerInfo?.orderType) === 'New Wig' ?
        'add a wig to sell' : 'add a wig to work on';
      return `Note: ${customerInfo?.orderType} orders require you to ${actionText} before proceeding.`;
    }
    
    return null;
  }, [customerInfo?.orderType, wigRequirementMet]);
  

  const [barcodeSearchResult, setBarcodeSearchResult] = useState(
    savedWigData?.barcodeSearchResult || {
      found: false,
      message: ''
    }
  );
  const [repairOptions, setRepairOptions] = useState(savedWigData?.repairOptions || []);
  const [isLoadingRepairs, setIsLoadingRepairs] = useState(false);
  const [accessoryOptions, setAccessoryOptions] = useState(savedWigData?.accessoryOptions || []);
  const [filteredAccessories, setFilteredAccessories] = useState(savedWigData?.filteredAccessories || []);
  const [selectedCategory, setSelectedCategory] = useState('All');

  
  // State for pricing information
  const [pricingData, setPricingData] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setWigData(prevData => {
      // Create updated data with the new field value
      const updatedData = {
        ...prevData,
        [name]: value
      };
      
      // If changing netSize or inventoryType to "Custom", synchronize both
      if ((name === 'netSize' || name === 'inventoryType') && value === 'Custom') {
        updatedData.netSize = 'Custom';
        updatedData.inventoryType = 'Custom';
      }
      
      return updatedData;
    });
  };

  const broadcastStateChange = useCallback(() => {
    const currentState = {
      wigData: wigData,
      selectedItems: selectedItems,
      selectedAccessories: selectedAccessories,
      barcodeSearchResult: barcodeSearchResult,
      pricingData: pricingData,
      showPricing: showPricing,
      repairOptions: repairOptions,
      accessoryOptions: accessoryOptions,
      filteredAccessories: filteredAccessories,
      selectedCategory: selectedCategory
    };
    
    // Notify parent immediately when critical data changes
    if (onStateChange && typeof onStateChange === 'function') {
      onStateChange(currentState);
    }
  }, [
    wigData, selectedItems, selectedAccessories, barcodeSearchResult, 
    pricingData, showPricing, repairOptions, accessoryOptions, 
    filteredAccessories, selectedCategory, onStateChange
  ]);


  useEffect(() => {
    // Broadcast state changes immediately for critical updates
    broadcastStateChange();
  }, [wigData, barcodeSearchResult, selectedItems, selectedAccessories]);

const handleBarcodeLookup = async () => { 
  setBarcodeSearchResult({
    found: false,
    message: 'Searching...'
  });
  
  try {
    const result = await apiService.get(`/api/lookup-barcode-enhanced/${wigData.barcode}`);
    // console.log("Barcode lookup result❤️❤️❤️❤️❤️❤️❤️:", result);
    if (result.found) {
      let message = 'Barcode found in system!';
      let canProceed = true;
      let warnings = [];
      
      if (result.isStockWig) {
        message = '✅ Stock Wig';
        if (normalizeOrderType(customerInfo?.orderType) === 'New Wig') {
          canProceed = result.availability.canSell.canProceed;
          if (!canProceed) {
            warnings.push(`❌ ${result.availability.canSell.reason}`);
          } else {
            message += ' - Available for sale';
          }
        } else {
          canProceed = result.availability.canWorkOn.canProceed;
          if (!canProceed) {
            warnings.push(`❌ ${result.availability.canWorkOn.reason}`);
          } else {
            message += ' - Available for service';
          }
        }
      } else {
        const wigOwnerName = typeof result.customerInfo === 'object'
          ? result.customerInfo?.name
          : result.customerInfo;
        const wigOwnerIsWholesaler = result.customerInfo?.isWholesaler;
        const isCustomerMismatch = wigOwnerName && wigOwnerName !== customerInfo?.customerName;
        if (isCustomerMismatch) {
          warnings.push(`⚠️ Customer Mismatch: This wig belongs to "${wigOwnerName}", but order is for "${customerInfo?.customerName}"`);
          if (!wigOwnerIsWholesaler) {
            canProceed = false;
          }
        }

        // Always check availability (active-order status), regardless of customer mismatch
        const availability = normalizeOrderType(customerInfo?.orderType) === 'New Wig'
          ? result.availability.canSell
          : result.availability.canWorkOn;

        if (!availability.canProceed) {
          canProceed = false;
          if (availability.reason) {
            warnings.push(`❌ ${availability.reason}`);
          }
        }

        if (warnings.length === 0) {
          if (wigOwnerName) {
            message = `✅ Wig belongs to ${wigOwnerName} - Available for ${normalizeOrderType(customerInfo?.orderType) === 'New Wig' ? 'sale' : 'service'}`;
          } else {
            message = `✅ Inventory wig - Available for ${normalizeOrderType(customerInfo?.orderType) === 'New Wig' ? 'sale' : 'service'}`;
          }
        }
      }

      if (warnings.length > 0) {
        message = warnings.join('\n');
      }

      setBarcodeSearchResult({
        found: true,
        message: message,
        isCustomerWig: !!(typeof result.customerInfo === 'object' ? result.customerInfo?.name : result.customerInfo) && !result.isStockWig,
        canProceed: canProceed,
        availability: result.availability
      });

      if (canProceed) {
        const serialCode = result.data.Wig_Serial_Code || result.data.Wig_Bar_Code;
        setWigData(prevData => ({
          ...prevData,
          barcode: result.data.Wig_Bar_Code || prevData.barcode,
          serialCode: serialCode,
          cutStyle: result.data.CutByStyle || '',
          wigType: result.data.Wig_Type || '',
          baseColor: result.data.Base_Color || '',
          hairLength: result.data.Wig_Length || '',
          netSize: result.data.Net_Size || '',
          hairType: result.data.Hair_Type || '',
          netType: result.data.Net_Type || '',
          laceType: result.data.Lace_Type || '',
          inventoryType: result.data.InventoryType || '',
          hairColor: result.data.Hair_Color || '',
          customerName: (typeof result.customerInfo === 'object' ? result.customerInfo?.name : result.customerInfo) || ''
        }));
      }
    } else {
      setBarcodeSearchResult({
        found: false,
        message: result.message || 'Barcode not found in system.'
      });
    }
  } catch (error) {
    console.error("Error looking up barcode:", error);
    setBarcodeSearchResult({
      found: false,
      message: 'Error connecting to server. Please try again later.'
    });
  }
};


  
 const handleAddToInventory = async () => {
    if (!wigData.barcode || !wigData.wigType || !wigData.hairLength || !wigData.baseColor) {
      alert('Please fill in all required fields: Barcode, Wig Type, Hair Length, and Base Color');
      return;
    }

    setIsAddingToInventory(true);

    try {
      // When adding from Work Order Entry, the wig is already sold to a customer
      // So we explicitly set the status to 'Sold'
      const wigDataWithSoldStatus = {
        ...wigData,
        InvStatus: 'Sold'
      };

      await inventoryService.addInventoryItem(wigDataWithSoldStatus);

      setAddInventorySuccess(true);
      setBarcodeSearchResult({
        found: true,
        message: 'Wig successfully added to inventory and ready for use!',
        canProceed: true
      });

      setTimeout(() => fetchPricingData(), 500);

    } catch (error) {
      console.error("Error adding to inventory:", error);
      alert(`Error adding to inventory: ${error.message}`);
    } finally {
      setIsAddingToInventory(false);
    }
  };

  // Toggle pricing display
  const togglePricing = () => {
    setShowPricing(!showPricing);
    
    if (!showPricing) {
      fetchPricingData();
      fetchRepairOptions();
      fetchAccessoryOptions();
    }
  };


// Function to fetch pricing data based on form selections
const fetchPricingData = async () => {
  try {
    setPricingData([]);
    let pricingComponents = [];

    // Base wig price
    const baseWigData = await apiService.post('/api/get-wig-base-price', {
      wigType: wigData.wigType,
      wigLength: wigData.hairLength,
      cutStyle: wigData.cutStyle
    });
    
    if (baseWigData.price) {
      pricingComponents.push({
        description: `${wigData.wigType} (${wigData.hairLength})`,
        price: `$${baseWigData.price.toFixed(2)}`,
        priceValue: baseWigData.price
      });
    }
      // 1. Add Dini Surcharge 
      if(wigData.cutStyle === 'DiNi') {
          pricingComponents.push({
          description: 'Dini Fee',
          price: '$2200.00',
          priceValue: 2200
        });
      };
      console.log('Wig Data for Pricing:', wigData);
    // 2. Check for color surcharges (red fee or blonde fee) - hardcoded based on length
    if (wigData.baseColor && (
        wigData.baseColor.toLowerCase().includes('red') || 
        wigData.baseColor.toLowerCase().includes('blonde')
    )) {
      let colorSurcharge = 0;
      const hairLength = parseInt(wigData.hairLength);
      
      if (hairLength >= 13 && hairLength <= 16) {
        colorSurcharge = 1000;
      } else if (hairLength >= 17 && hairLength <= 20) {
        colorSurcharge = 1900;
      } else if (hairLength >= 21) {
        colorSurcharge = 3100;
      }

      if (wigData.wigType?.toLowerCase().includes('topper') && (hairLength >= 13 && hairLength <= 16)) {
        colorSurcharge = 500;
      } else if (wigData.wigType?.toLowerCase().includes('topper') && (hairLength >= 17 && hairLength <= 20)) {
        colorSurcharge = 950;
      } else if (wigData.wigType?.toLowerCase().includes('topper') && (hairLength >= 21)) {
        colorSurcharge = 1550;
      }
      
      if (colorSurcharge > 0) {
        pricingComponents.push({
          description: `${formatColorName(wigData.baseColor)} Color Surcharge`,
          price: `$${colorSurcharge.toFixed(2)}`,
          priceValue: colorSurcharge
        });
      }
    }
    
    // 3. Check for inventory type surcharges (hardcoded values)
    if (wigData.inventoryType === 'Handmade') {
      pricingComponents.push({
        description: 'Handmade Fee',
        price: '$3000.00',
        priceValue: 3000
      });
    } else if (wigData.inventoryType === 'Custom' && wigData.wigType?.toLowerCase().includes('topper')) {
      pricingComponents.push({
        description: 'Custom Topper Surcharge',
        price: '$780.00',
        priceValue: 780
      });
    } else if (wigData.inventoryType === 'Custom' || wigData.netSize === 'Custom') {
      pricingComponents.push({
        description: 'Custom Order Surcharge',
        price: '$1300.00',
        priceValue: 1300
      });
    }
    
    // 4. Add virtual consultation fee (hardcoded as requested)
    let consultPrice = 0;
    let consultDesc = '';
    
    switch(wigData.virtualConsult) {
      case 'Half Virtual Consult':
        consultPrice = 400;
        consultDesc = 'Half Virtual Consultation';
        break;
      case 'Full Virtual Consult':
        consultPrice = 1000;
        consultDesc = 'Full Virtual Consultation';
        break;
      default:
        // No surcharge for "No Virtual Consult"
        break;
    }
    
    if (consultPrice > 0) {
      pricingComponents.push({
        description: consultDesc,
        price: `$${consultPrice.toFixed(2)}`,
        priceValue: consultPrice
      });
    }
    
    // 5. Calculate total price
    const totalPrice = pricingComponents.reduce((sum, item) => sum + (item.priceValue || 0), 0);
    
    // Add total as the last item
    if (pricingComponents.length > 0) {
      pricingComponents.push({
        description: 'TOTAL',
        price: `$${totalPrice.toFixed(2)}`,
        priceValue: totalPrice,
        isTotal: true // Flag to style this row differently
      });
    } else {
      // If no pricing components were found, show a message
      pricingComponents.push({
        description: 'No pricing available for the selected options',
        price: 'N/A'
      });
    }
    
    // Update state with all pricing components
    setPricingData(pricingComponents);
    setEditablePricingData([...pricingComponents]);
    
  } catch (error) {
    console.error("Error calculating pricing:", error);
    setPricingData([
      { 
        description: 'Error calculating price. Please try again or contact support.',
        price: 'Error'
      }
    ]);
  }
};

const handlePriceEdit = (index, newPrice) => {
  const updatedPricing = [...editablePricingData];
  const numericPrice = parseFloat(newPrice) || 0;
  updatedPricing[index].priceValue = numericPrice;
  updatedPricing[index].price = `$${numericPrice.toFixed(2)}`;
  
  // Recalculate total
  const totalIndex = updatedPricing.findIndex(item => item.isTotal);
  if (totalIndex !== -1) {
    const newTotal = updatedPricing
      .filter(item => !item.isTotal)
      .reduce((sum, item) => sum + (item.priceValue || 0), 0);
    updatedPricing[totalIndex].priceValue = newTotal;
    updatedPricing[totalIndex].price = `$${newTotal.toFixed(2)}`;
  }
  
  setEditablePricingData(updatedPricing);
};

const handleStyleEdit = (index, newDescription) => {
  const updatedPricing = [...editablePricingData];
  updatedPricing[index].description = newDescription;
  setEditablePricingData(updatedPricing);
};

const togglePriceEditing = () => {
  if (isEditingPrice) {
    // Save changes back to main pricing data
    setPricingData([...editablePricingData]);
  } else {
    // Create fresh editable copy
    setEditablePricingData([...pricingData]);
  }
  setIsEditingPrice(!isEditingPrice);
};

useEffect(() => {
  const wigMet = checkWigRequirement();
  setWigRequirementMet(wigMet);
}, [checkWigRequirement]);

useEffect(() => {
  if (wigData.barcode !== savedWigData?.wigData?.barcode) {
    setShowAddToInventory(false);
    setAddInventorySuccess(false);
    setIsAddingToInventory(false);
  }
}, [wigData.barcode, savedWigData?.wigData?.barcode]);

useEffect(() => {
  // Only calculate pricing if we have the minimum required fields
  if (wigData.wigType && wigData.hairLength) {
    fetchPricingData();
  } else {
    // Clear pricing data if essential fields are missing
    setPricingData([]);
  }
}, [wigData.wigType, wigData.hairLength, wigData.cutStyle, wigData.baseColor, wigData.inventoryType,  wigData.netSize, wigData.virtualConsult ]);


// Updated fetchOrderHistory function - replace the existing one
useEffect(() => {
  // Load initial order history when component mounts or customer changes
  refreshOrderHistory();
}, [refreshOrderHistory])

  // Select an item from order history
  const selectOrderHistoryItem = (item) => {
    // Populate form with selected order details
    console.log("Selected order:", item);

    // Block if ANY order with the same barcode OR serial code is still open
    const completedStatuses = ['Completed', 'completed'];
    const openOrdersForSameWig = orderHistory.filter(o =>
      o.orderNo !== item.orderNo &&
      !completedStatuses.includes(o.orderStatus) &&
      (
        (item.barCode && o.barCode && o.barCode === item.barCode) ||
        (item.serialCode && o.serialCode && o.serialCode === item.serialCode)
      )
    );

    if (openOrdersForSameWig.length > 0) {
      const openList = openOrdersForSameWig
        .map(o => `Order #${o.orderNo} (${o.orderStatus})`)
        .join(', ');
      setBarcodeSearchResult({
        found: false,
        canProceed: false,
        message: `❌ Cannot select wig ${item.barCode || item.serialCode} — open orders must be completed first: ${openList}`
      });
      return;
    }

    // Also block if the selected order itself is not completed
    if (!completedStatuses.includes(item.orderStatus)) {
      setBarcodeSearchResult({
        found: false,
        canProceed: false,
        message: `❌ Order #${item.orderNo} is currently "${item.orderStatus}" — please complete or close it before creating a new one.`
      });
      return;
    }

    // Would populate form fields in a real app
    setWigData({
      ...wigData,
      barcode: item.barCode,
      serialCode: item.serialCode
    });

    setBarcodeSearchResult({
      found: true,
      message: `Order ${item.orderNo} selected for service/repair.`
    });

     setShouldTriggerLookup(true);
  };
  // Add a state to track if lookup should be triggered
  const [shouldTriggerLookup, setShouldTriggerLookup] = useState(false);

  useEffect(() => {
    if (shouldTriggerLookup && wigData.barcode) {
      handleBarcodeLookup();
      setShouldTriggerLookup(false);
    }
  }, [wigData.barcode, shouldTriggerLookup]);

  // Auto-fill barcode from draft when it looks like a real barcode number
  useEffect(() => {
    const UNAVAILABLE = /^(none|no|n\/a|don'?t\s+have|not\s+available|unavailable|missing|unknown)/i;
    if (draftBarcodeNote && !UNAVAILABLE.test(draftBarcodeNote.trim()) && draftBarcodeNote.trim().length >= 3 && !wigData.barcode) {
      setWigData(prev => ({ ...prev, barcode: draftBarcodeNote.trim() }));
      setShouldTriggerLookup(true);
    }
  }, [draftBarcodeNote]);
  


  // Handle category selection for accessories
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'All') {
      setFilteredAccessories(accessoryOptions);
    } else {
      setFilteredAccessories(
        accessoryOptions.filter(accessory => accessory.Category === category)
      );
    }
  };

  // State for new repair form
  const [newRepair, setNewRepair] = useState({
    description: '',
    price: ''
  });

  const addCustomRepairToOrder = () => {
    if (!newRepair.description || newRepair.price === '' || newRepair.price === null) {
      alert('Please enter both description and price');
      return;
    }
    
    const price = parseFloat(newRepair.price);
    if (isNaN(price)) {
      alert('Please enter a valid price (use negative numbers for discounts)');
      return;
    }    
    
    // Determine item type based on price
    const itemType = price < 0 ? 'discount' : 'repair';
    const displayDescription = price < 0 && !newRepair.description.toLowerCase().includes('discount') 
      ? `Discount: ${newRepair.description}` 
      : newRepair.description;
    
    const newItem = {
      id: Date.now(),
      type: itemType,
      description: displayDescription,
      style: displayDescription,
      price: price,
      quantity: 1
    };
    
    setSelectedItems(prevItems => [...prevItems, newItem]);
    
    // Clear the form
    setNewRepair({
      description: '',
      price: ''
    });
    
  
  };


  
const fetchRepairOptions = async () => {
  setIsLoadingRepairs(true);
  try {
    const data = await apiService.get('/api/get-repair-options');
    setRepairOptions(data);
  } catch (error) {
    console.error("Error fetching repair options:", error);
    setRepairOptions([]);
  } finally {
    setIsLoadingRepairs(false);
  }
};

// Replace fetchAccessoryOptions
const fetchAccessoryOptions = async () => {
  try {
    const data = await apiService.get('/api/get-accessories');
    setAccessoryOptions(data);
    setFilteredAccessories(data);
  } catch (error) {
    console.error("Error fetching accessories:", error);
    setAccessoryOptions([]);
    setFilteredAccessories([]);
  }
};

  
  const addInventoryItem = (item) => {
    const newItem = {
      id: Date.now(),
      type: item.type || 'inventory',
      // Use RepairDescription as the description for repair items
      description: item.style, // This will contain the RepairDescription
      style: item.style,
      baseType: item.type || '',
      length: item.length || '',
      inventoryType: item.inventoryType || '',
      price: parseFloat(item.price?.replace('$', '')) || 0,
      includeLace: item.includeLace || 'No',
      quantity: 1
    };
    
    setSelectedItems(prevItems => [...prevItems, newItem]);
    console.log("Added to order:", newItem);
  };

  const [newAccessory, setNewAccessory] = useState({
    category: '',
    description: '',
    price: ''
  });


  const addCustomAccessoryToOrder = () => {
    if (!newAccessory.category || !newAccessory.description || !newAccessory.price) {
      alert('Please fill in all fields');
      return;
    }
    
    const price = parseFloat(newAccessory.price);
    if (isNaN(price) || price <= 0) {
      alert('Please enter a valid price');
      return;
    }
    
    const accessoryItem = {
      id: Date.now(),
      type: 'accessory',
      description: newAccessory.description,
      category: newAccessory.category,
      price: price,
      quantity: 1
    };
    
    setSelectedAccessories(prevAccessories => [...prevAccessories, accessoryItem]);
    
    // Clear the form
    setNewAccessory({
      category: '',
      description: '',
      price: ''
    });
    
    console.log("Added custom accessory to order:", accessoryItem);
  };

  
  const addAccessory = (accessory) => {
    const newAccessory = {
      id: Date.now(), // Unique identifier
      type: 'accessory',
      category: accessory.category || '',
      description: accessory.description || '',
      price: parseFloat(accessory.price?.replace('$', '')) || 0,
      quantity: 1
    };
    
    setSelectedAccessories(prevAccessories => [...prevAccessories, newAccessory]);
    console.log("Added accessory to order:", newAccessory);
  };

  // Remove items methods
  const removeInventoryItem = (itemId) => {
    setSelectedItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const removeAccessory = (accessoryId) => {
    setSelectedAccessories(prevAccessories => 
      prevAccessories.filter(accessory => accessory.id !== accessoryId)
    );
  };

  // Update quantity methods
  const updateItemQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setSelectedItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const updateAccessoryQuantity = (accessoryId, newQuantity) => {
    if (newQuantity < 1) return;

    setSelectedAccessories(prevAccessories =>
      prevAccessories.map(accessory =>
        accessory.id === accessoryId ? { ...accessory, quantity: newQuantity } : accessory
      )
    );
  };

  // Update price methods
  const updateItemPrice = (itemId, newPrice) => {
    // Allow empty string for blank field, otherwise convert to number
    const price = newPrice === '' ? 0 : (parseFloat(newPrice) || 0);

    setSelectedItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, price: price } : item
      )
    );
  };

  const updateAccessoryPrice = (accessoryId, newPrice) => {
    // Allow empty string for blank field, otherwise convert to number
    const price = newPrice === '' ? 0 : (parseFloat(newPrice) || 0);

    setSelectedAccessories(prevAccessories =>
      prevAccessories.map(accessory =>
        accessory.id === accessoryId ? { ...accessory, price: price } : accessory
      )
    );
  };

  
  const addCurrentPricingToOrder = () => {
    // CRITICAL: Check if barcode exists in inventory first
    if (wigData.barcode && !barcodeSearchResult.found && !addInventorySuccess) {
      alert('⚠️ This barcode must be added to inventory before proceeding. Please scroll up and click "Confirm - Add to Inventory".');
      return;
    }

    // Ensure we have all required fields
    if (!wigData.wigType || !wigData.hairLength) {
      alert('Please select all required fields (Wig Type and Hair Length)');
      return;
    }

    // Use edited pricing data if available
    const currentPricingData = isEditingPrice ? editablePricingData : pricingData;
    
    // Filter out the total row - we only want individual components
    const pricingComponents = currentPricingData.filter(item => !item.isTotal);
    
    if (pricingComponents.length === 0) {
      alert('Unable to calculate price for the current configuration');
      return;
    }
    
    // Create separate items for each pricing component
    const newItems = pricingComponents.map((component, index) => ({
      id: Date.now() + index, // Ensure unique IDs
      type: 'inventory',
      description: component.description,
      barcode: index === 0 ? wigData.barcode : '', // Only show barcode on first item
      details: index === 0 ? {
        wigType: wigData.wigType,
        hairLength: wigData.hairLength,
        cutStyle: wigData.cutStyle,
        baseColor: wigData.baseColor,
        inventoryType: wigData.inventoryType,
        netSize: wigData.netSize,
        hairType: wigData.hairType,
        netType: wigData.netType,
        laceType: wigData.laceType,
        virtualConsult: wigData.virtualConsult,
        barcode: wigData.barcode
      } : null, // Only show details on the main wig item
      price: component.priceValue || 0,
      quantity: 1,
      isWigComponent: true, // Flag to identify these as related wig components
      wigGroupId: Date.now() // Group ID to link related components together
    }));
    
    // Add all components to selected items
    setSelectedItems(prevItems => [...prevItems, ...newItems]);
    
    console.log("Added wig pricing breakdown to order:", newItems);
  };


  const addCurrentConfigurationToOrder = () => {
    // CRITICAL: Check if barcode exists in inventory first
    if (wigData.barcode && !barcodeSearchResult.found && !addInventorySuccess) {
      alert('⚠️ This barcode must be added to inventory before proceeding. Please scroll up and click "Confirm - Add to Inventory".');
      return;
    }

    // Ensure we have all required fields
    if (!wigData.wigType || !wigData.hairLength) {
      alert('Please select all required fields (Wig Type and Hair Length)');
      return;
    }

    // Create a single item object for service orders (no pricing breakdown needed for service)
    const newItem = {
      id: Date.now(),
      type: 'inventory',
      description: `${wigData.wigType} - ${wigData.hairLength}" - ${wigData.cutStyle || 'Standard'} (Service Item)`,
      barcode: wigData.barcode,
      details: {
        wigType: wigData.wigType,
        hairLength: wigData.hairLength,
        cutStyle: wigData.cutStyle,
        baseColor: wigData.baseColor,
        inventoryType: wigData.inventoryType,
        netSize: wigData.netSize,
        hairType: wigData.hairType,
        netType: wigData.netType,
        laceType: wigData.laceType,
        virtualConsult: wigData.virtualConsult,
        barcode: wigData.barcode
      },
      price: 0, // No charge for service items
      quantity: 1,
      isServiceItem: true // Flag to indicate this is a service item without charge
    };
    
    // Add to selected items
    setSelectedItems(prevItems => [...prevItems, newItem]);
    
    console.log("Added current wig configuration to order (service item):", newItem);
  };


  

  // Calculate totals
  const calculateTotal = () => {
    const inventoryTotal = selectedItems.reduce(
      (sum, item) => sum + (item.price * item.quantity), 0
    );
    const accessoryTotal = selectedAccessories.reduce(
      (sum, accessory) => sum + (accessory.price * accessory.quantity), 0
    );

    return inventoryTotal + accessoryTotal;
  };

  // ── Wig Repair warranty gate ──────────────────────────────────────────────
  // Look up the wig's warranty whenever a barcode has been confirmed.
  useEffect(() => {
    const bc = (wigData.barcode || '').trim();
    if (!bc || !barcodeSearchResult.found || barcodeSearchResult.canProceed === false) {
      setWarrantyCheck(null);
      return;
    }

    let cancelled = false;
    setWarrantyCheck({ loading: true, barcode: bc });

    apiService.get(`/api/warranty/by-barcode/${encodeURIComponent(bc)}`)
      .then(res => { if (!cancelled) setWarrantyCheck({ loading: false, barcode: bc, ...res }); })
      .catch(err => {
        console.error('Warranty lookup failed:', err);
        if (!cancelled) setWarrantyCheck({ loading: false, barcode: bc, error: true });
      });

    return () => { cancelled = true; };
  }, [wigData.barcode, barcodeSearchResult.found, barcodeSearchResult.canProceed]);

  // "Aidel Wig Repair" follows the same repair rules as "Wig Repair".
  const isWigRepair = normalizeOrderType(customerInfo?.orderType) === 'Wig Repair';

  // Stock wigs (customer "Stock Wig" / OrderCustomerNo 6252) are internal and
  // never need a priced repair line.
  const isStockWigOrder =
    String(customerInfo?.customerNo ?? '').trim() === '6252' ||
    String(customerInfo?.ID ?? '').trim() === '6252' ||
    String(customerInfo?.customerName ?? '').trim().toLowerCase() === 'stock wig';

  const underWarranty =
    warrantyCheck && !warrantyCheck.loading
      ? warrantyCheck.underWarranty === true
      : draftWarrantyInfo?.status === 'active';

  const warrantyResolved =
    (warrantyCheck && !warrantyCheck.loading) || draftWarrantyInfo != null;

  const warrantyExpiresLabel =
    (warrantyCheck && !warrantyCheck.loading && warrantyCheck.expiresDate) ||
    draftWarrantyInfo?.expiresDate ||
    null;

  // Exempt from the Wig Repair warranty / $0-total gate: 'chenw', and all
  // Israel-location users (the warranty blocker does not apply to that branch).
  const warrantyGateExempt =
    String(getCurrentUser() || '').trim().toLowerCase() === 'chenw' ||
    isIsraelUser();

  const repairGate = (() => {
    if (!isWigRepair || isStockWigOrder || warrantyGateExempt) return { blocked: false };

    const total = calculateTotal();

 

    // Line items exist but nothing is being charged: only allowed under warranty.
    if (total <= 0 && !underWarranty) {
      if (!warrantyResolved && (wigData.barcode || '').trim()) {
        return { blocked: true, pending: true, message: 'Checking this wig\'s warranty...' };
      }
      return {
        blocked: true,
        message: 'This wig is not under warranty and the repair total is $0. Add the repair cost before continuing.',
      };
    }

    return { blocked: false };
  })();

  const canProceedToNext = wigRequirementMet && !repairGate.blocked;

  const handlePreviousClick = () => {
    // Save all state before going back
    const dataToSave = {
      wigData: wigData,
      selectedItems: selectedItems,
      selectedAccessories: selectedAccessories,
      barcode: wigData.barcode,
      serialCode: wigData.serialCode,
      barcodeSearchResult: barcodeSearchResult,
      pricingData: pricingData,
      showPricing: showPricing,
      repairOptions: repairOptions,
      accessoryOptions: accessoryOptions,
      filteredAccessories: filteredAccessories,
      selectedCategory: selectedCategory
    };
    onPrevious(dataToSave); // Pass the data back to parent
  };

  // Warranty coverage badge for the customer order history table. The state is
  // resolved per wig barcode by the API ('active' | 'expired' | 'none').
  const renderWarrantyCoverage = (order) => {
    const base = {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 700,
    };
    if (order.warrantyState === 'active') {
      return <span style={{ ...base, background: '#d1e7dd', color: '#0f5132' }}>Under warranty</span>;
    }
    if (order.warrantyState === 'expired') {
      return (
        <span style={{ ...base, background: '#f8d7da', color: '#842029' }}>
          Expired{order.warrantyExpiresDate ? ` ${order.warrantyExpiresDate}` : ''}
        </span>
      );
    }
    return <span style={{ ...base, background: '#e9ecef', color: '#6c757d' }}>No warranty</span>;
  };

  return (
    <div className="woe-container slide-in">
      <h2>Wig Details</h2>
      
      {/* Customer Summary Section */}
      <div className="customer-summary">
        <h3>Customer Information</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Order #:</span>
            <span className="summary-value">{customerInfo?.orderNo || 'New Order'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Name:</span>
            <span className="summary-value">{customerInfo?.customerName || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Phone:</span>
            <span className="summary-value">{customerInfo?.phone || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Order Type:</span>
            <span className="summary-value">{customerInfo?.orderType || 'N/A'}</span>
          </div>
        </div>
      </div>
      
      <div className="form-row">
          <div className="form-group">
            
            <label htmlFor="barcode">Barcode:</label>
              {draftBarcodeNote && (
                <div style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #f59e0b',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  marginBottom: '8px',
                  fontSize: '13px',
                  color: '#78350f',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                  <span style={{ fontSize: '15px', flexShrink: 0 }}>🗣</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span><strong>Stylist provided:</strong> "{draftBarcodeNote}"</span>
                      {draftWarrantyInfo?.status === 'active' && (
                        <span style={{
                          background: '#dbeafe', color: '#1e40af',
                          borderRadius: 20, padding: '1px 10px',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          🛡 Under warranty — expires {draftWarrantyInfo.expiresDate}
                        </span>
                      )}
                      {draftWarrantyInfo?.status === 'expired' && (
                        <span style={{
                          background: '#fee2e2', color: '#991b1b',
                          borderRadius: 20, padding: '1px 10px',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          ⚠️ Out of warranty
                        </span>
                      )}
                      {draftServicePrice != null && (
                        <span style={{
                          background: '#dcfce7', color: '#166534',
                          borderRadius: 20, padding: '1px 10px',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          💰 {draftServicePrice || 'No charge'}
                        </span>
                      )}
                    </div>
                    {!/^(none|no|n\/a|don'?t\s+have|not\s+available|unavailable|missing|unknown)/i.test(draftBarcodeNote.trim()) && draftBarcodeNote.trim().length >= 3 ? (
                      <div style={{ marginTop: 3, color: '#92400e', fontSize: '12px' }}>
                        {draftServicePrice != null
                          ? 'Barcode pre-filled and looked up below.'
                          : 'Barcode pre-filled below — verify and look it up.'}
                      </div>
                    ) : (
                      <div style={{ marginTop: 3, color: '#92400e', fontSize: '12px' }}>
                        Stylist didn't have the barcode — please look it up.
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="barcode-input-group">
                <input 
                  type="text" 
                  id="barcode" 
                  name="barcode"
                  value={wigData.barcode}
                  onChange={handleInputChange}
                />
                
                {barcodeSearchResult.message && (
                  <div className="barcode-input-group">
                    <span className={barcodeSearchResult.found && barcodeSearchResult.canProceed !== false ? "found-text" : "not-found-text"}>
                      {barcodeSearchResult.message}
                    </span>
                    {!barcodeSearchResult.found && barcodeSearchResult.canProceed !== false && !showAddToInventory && wigData.barcode && (
                      <button 
                        type="button" 
                        className="add-inventory-prompt-button"
                        onClick={() => setShowAddToInventory(true)}
                        style={{ marginLeft: '10px', padding: '5px 10px' }}
                      >
                        Add to Inventory?
                      </button>
                    )}
                  </div>
                )}
              </div>
          </div>
          <button 
                type="button" 
                className="lookup-button"
                onClick={handleBarcodeLookup}
                >
                LookUp
              </button>
          <div className="form-group">
            <label htmlFor="serialCode">Serial Code:</label>
            <div className="barcode-input-group">
            <input 
              type="text" 
              id="serialCode" 
              name="serialCode"
              value={wigData.serialCode}
              onChange={handleInputChange}
            />
            </div>
            
          </div>
          
          
        </div>

       {/* Add to Inventory Confirmation */}
          {showAddToInventory && (
            <div className="add-inventory-section" style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px', 
              margin: '20px 0',
              border: '2px solid #007bff'
            }}>
              <h3>Add New Wig to Inventory</h3>
              <p>Barcode <strong>{wigData.barcode}</strong> not found. Fill in the required details to add it to inventory:</p>
              
              {!addInventorySuccess ? (
                <div>
                  <div className="required-fields-notice" style={{ 
                    backgroundColor: '#fff3cd', 
                    padding: '10px', 
                    borderRadius: '4px', 
                    marginBottom: '15px' 
                  }}>
                    <strong>Required fields:</strong> Cut Style, Wig Type, Hair Length, Base Color
                  </div>
                  
                  <div className="form-actions" style={{ marginTop: '15px' }}>
                    <button 
                      type="button" 
                      className="confirm-add-inventory"
                      onClick={() => handleAddToInventory()}
                      disabled={!wigData.cutStyle || !wigData.wigType || !wigData.hairLength || !wigData.baseColor || isAddingToInventory}
                      style={{ 
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '10px 20px',
                        marginRight: '10px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: wigData.cutStyle && wigData.wigType && wigData.hairLength && wigData.baseColor ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {isAddingToInventory ? 'Adding to Inventory...' : 'Confirm - Add to Inventory'}
                    </button>
                    
                    <button 
                      type="button" 
                      className="cancel-add-inventory"
                      onClick={() => {
                        setShowAddToInventory(false);
                        setAddInventorySuccess(false);
                      }}
                      style={{ 
                        backgroundColor: '#6c757d',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: '#d4edda', 
                  padding: '15px', 
                  borderRadius: '4px',
                  color: '#155724' 
                }}>
                  ✅ Barcode {wigData.barcode} successfully added to inventory! You can now proceed with pricing.
                </div>
              )}
            </div>
          )}
   {/* Wig Details Form */}
      {(showOrderHistory &&
         normalizeOrderType(customerInfo?.orderType) !== 'Wig Repair' &&
         customerInfo?.orderType !== 'Wash & Set' &&
         customerInfo?.orderType !== 'Recut' &&
         customerInfo?.orderType !== 'Accessory' &&
         customerInfo?.orderType !== 'Consultation' ||
         showAddToInventory) && (

          <div className="order-history-section">
    
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cutStyle">Cut Style:</label>
              <select 
                id="cutStyle" 
                name="cutStyle"
                value={wigData.cutStyle}
                onChange={handleInputChange}
                style={showAddToInventory && !wigData.cutStyle ? {border: '2px solid #dc3545'} : {}}
              >
                <option value="">Select Cut Style</option>
                {WIG_OPTIONS.cutStyles.map((style, index) => (
                  <option key={index} value={style}>{style}</option>
                ))}
              </select>
              {showAddToInventory && !wigData.cutStyle && (
                <small style={{color: '#dc3545'}}>* Required</small>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="baseColor">Base Color:</label>
              <select 
                id="baseColor" 
                name="baseColor"
                value={wigData.baseColor}
                onChange={handleInputChange}
                style={showAddToInventory && !wigData.baseColor ? {border: '2px solid #dc3545'} : {}}
              >
                <option value="">Select Base Color</option>
                {getColorOptions(wigData.wigType).map((color, index) => (
                  <option key={index} value={color}>{color}</option>
                ))}
              </select>
              {showAddToInventory && !wigData.baseColor && (
                <small style={{color: '#dc3545'}}>* Required</small>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="inventoryType">Inventory Type:</label>
              <select 
                id="inventoryType" 
                name="inventoryType"
                value={wigData.inventoryType}
                onChange={handleInputChange}
              >
                <option value="">Select Inventory Type</option>
                {WIG_OPTIONS.inventoryTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="wigType">Wig Type:</label>
              <select 
                id="wigType" 
                name="wigType"
                value={wigData.wigType}
                onChange={handleInputChange}
                style={showAddToInventory && !wigData.wigType ? {border: '2px solid #dc3545'} : {}}
              >
                <option value="">Select Wig Type</option>
                {WIG_OPTIONS.wigTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
                {showAddToInventory && !wigData.wigType && (
                  <small style={{color: '#dc3545'}}>* Required</small>
                )}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="hairLength">Hair Length:</label>
              <select 
                id="hairLength" 
                name="hairLength"
                value={wigData.hairLength}
                onChange={handleInputChange}
                style={showAddToInventory && !wigData.hairLength ? {border: '2px solid #dc3545'} : {}}
              >
                <option value="">Select Length</option>
                {WIG_OPTIONS.hairLengths.map((length, index) => (
                  <option key={index} value={length}>{length}</option>
                ))}
                {showAddToInventory && !wigData.hairLength && (
                  <small style={{color: '#dc3545'}}>* Required</small>
                )}

              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="netSize">Net Size:</label>
              <select 
                id="netSize" 
                name="netSize"
                value={wigData.netSize}
                onChange={handleInputChange}
              >
                <option value="">Select Net Size</option>
                {WIG_OPTIONS.netSizes.map((size, index) => (
                  <option key={index} value={size}>{size}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="hairType">Hair Type:</label>
              <select 
                id="hairType" 
                name="hairType"
                value={wigData.hairType}
                onChange={handleInputChange}
              >
                <option value="">Select Hair Type</option>
                {WIG_OPTIONS.hairTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>
  
  
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="netType">Net Type:</label>
              <select 
                id="netType" 
                name="netType"
                value={wigData.netType}
                onChange={handleInputChange}
              >
                <option value="">Select Net Type</option>
                {WIG_OPTIONS.netTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="laceType">Lace Type:</label>
              <select 
                id="laceType" 
                name="laceType"
                value={wigData.laceType}
                onChange={handleInputChange}
              >
                <option value="">Select Lace Type</option>
                {WIG_OPTIONS.laceTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
          
          </div>
          
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="virtualConsult">Virtual Consultation:</label>
              <select 
                id="virtualConsult" 
                name="virtualConsult"
                value={wigData.virtualConsult}
                onChange={handleInputChange}
              >
                {WIG_OPTIONS.virtualOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
  
  
         
        </div>
         )}
     


        {/* Customer Order History Section */}
        {showOrderHistory && normalizeOrderType(customerInfo?.orderType) !== 'New Wig' &&
        customerInfo?.orderType !== 'New Sale Wig' && (
          <div className="order-history-section">
            <h3>Customer Order History, Select a Wig for Service or Repair</h3>
            <div className="order-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Order No</th>
                    <th>Bar Code</th>
                    <th>Order Type</th>
                    <th>Status</th>
                    <th>Order Date</th>
                    <th>Due Date</th>
                    <th>Warranty Start</th>
                    <th>Warranty Expires</th>
                    <th>Warranty Coverage</th>                    
                    <th>Serial Code</th>
                    <th>Auto Complete</th>
                  </tr>
                </thead>
                <tbody>
                  {orderHistory.length > 0 ? (
                    orderHistory.map((order, index) => (
                      <tr key={`${order.orderNo}-${index}`}>
                        <td>
                          <button 
                            type="button" 
                            className="select-button"
                            onClick={() => selectOrderHistoryItem(order)}
                          >
                            Select
                          </button>
                        </td>
                        <td><a href={`/work-order-profile/${order.orderNo}`} target="_blank" rel="noreferrer">{order.orderNo}</a></td>
                        <td>{order.barCode}</td>
                        <td>{order.orderType}</td>
                        <td>
                          <span style={{
                            color: order.orderStatus === 'Completed' ? '#28a745' : 
                                  order.orderStatus === 'In-Process' ? '#ffc107' : '#6c757d',
                            fontWeight: 'bold'
                          }}>
                            {order.orderStatus}
                          </span>
                        </td>
                        <td>{order.orderDate}</td>
                        <td>{order.dueDate}</td>
                        <td>{order.warrantyStartDate || '—'}</td>
                        <td>{order.warrantyExpiresDate || '—'}</td>
                        <td>{renderWarrantyCoverage(order)}</td>
                        <td>{order.serialCode}</td>
                        <td>
                          {order.orderStatus !== 'Completed' ? (
                            <AutoCompleteButton
                              orderNo={order.orderNo}
                              variant="success"
                              size="sm"
                              showText={true}
                              className="select-button"
                              refreshOrderHistory={refreshOrderHistory} // 🔥 Pass the refresh function
                              onAutoCompleteSuccess={(orderNo, data) => {
                                console.log(`✅ Order ${orderNo} auto-completed successfully`);
                                // The refreshOrderHistory will be called by the button itself
                                // Additional logic can go here if needed
                              }}
                              onUndoSuccess={(orderNo, data) => {
                                console.log(`🔄 Order ${orderNo} undo completed successfully`);
                                // The refreshOrderHistory will be called by the button itself
                                // Additional logic can go here if needed
                              }}
                            />
                          ) : (
                            <span style={{ 
                              color: '#28a745', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px'
                            }}>
                              ✓ Completed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12">No Previous Orders Found for this Customer!</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      <div className="form-row">


      </div>
   

      
      <div className="form-row">


      </div>


        {/* Pricing display would go here when selections change */}
        <div className="form-group pricing-preview">
          {pricingData.length > 0 && (addInventorySuccess || !showAddToInventory) && (
            <div className="pricing-table">
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4>Pricing Breakdown</h4>
                <button
                  type="button"
                  className="edit-price-button"
                  onClick={togglePriceEditing}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: isEditingPrice ? '#28a745' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {isEditingPrice ? 'Save Changes' : 'Edit Prices'}
                </button>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Style</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {(isEditingPrice ? editablePricingData : pricingData).map((item, index) => (
                    <tr key={index} className={item.isTotal ? 'total-row' : ''}>
                      <td>
                        {isEditingPrice && !item.isTotal ? (
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => handleStyleEdit(index, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '4px',
                              border: '1px solid #ccc',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          item.description
                        )}
                      </td>
                      <td>
                        {isEditingPrice && !item.isTotal ? (
                          <input
                            type="number"
                            step="0.01"
                            value={item.priceValue || 0}
                            onChange={(e) => handlePriceEdit(index, e.target.value)}
                            style={{
                              width: '80px',
                              padding: '4px',
                              border: '1px solid #ccc',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          item.price
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Warning blocker when inventory needs to be added */}
              {showAddToInventory && !addInventorySuccess && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '2px solid #ffc107',
                  padding: '15px',
                  borderRadius: '8px',
                  margin: '15px 0',
                  textAlign: 'center'
                }}>
                  <strong style={{ color: '#856404', fontSize: '16px' }}>
                    ⚠️ Barcode must be added to inventory first!
                  </strong>
                  <p style={{ color: '#856404', marginTop: '8px', marginBottom: '0' }}>
                    Scroll up and click "Confirm - Add to Inventory" to proceed.
                  </p>
                </div>
              )}

               {normalizeOrderType(customerInfo?.orderType) === 'New Wig' || customerInfo?.orderType === 'New Sale Wig' ? (
                <button
                  type="button"
                  className={`add-to-item-list ${!wigRequirementMet ? 'required-action' : ''}`}
                  onClick={() => addCurrentPricingToOrder()}
                  disabled={(showAddToInventory && !addInventorySuccess) || barcodeSearchResult.canProceed === false}
                  style={(showAddToInventory && !addInventorySuccess) || barcodeSearchResult.canProceed === false ? {
                    opacity: 0.5,
                    cursor: 'not-allowed'
                  } : {}}
                >
                  Sell wig {showAddToInventory && !addInventorySuccess ? '(Add to inventory first)' : ''}
                </button>
              ) : (
                <button
                  type="button"
                  className={`add-to-item-list-2 ${!wigRequirementMet ? 'required-action' : ''}`}
                  onClick={() => addCurrentConfigurationToOrder()}
                  disabled={(showAddToInventory && !addInventorySuccess) || barcodeSearchResult.canProceed === false}
                  style={(showAddToInventory && !addInventorySuccess) || barcodeSearchResult.canProceed === false ? {
                    opacity: 0.5,
                    cursor: 'not-allowed'
                  } : {}}
                >
                  Work on this wig {showAddToInventory && !addInventorySuccess ? '(Add to inventory first)' : ''}
                </button>
              )}
            </div>
          )}
        </div>


      {/* Wig Pricing Section */}
      <div className="order-history-section">

          
        {/* Price List Toggle Button */}
        <div className="form-row">
          <button 
            type="button" 
            className="select-button"
            onClick={togglePricing}
          >
            {showPricing ? 'Close Pricing Lists' : 'Show Pricing List'}
          </button>
        </div>
        
        
        
        {/* Pricing Display Section Wig Repair Options & Available Accessories */}
        {showPricing && (
          <div className="pricing-lists-section">
            <div className="pricing-grid">
              <div className="pricing-column">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>Wig Repair Options</h3>
                  <input
                    type="text"
                    placeholder="Search repairs..."
                    value={repairSearchTerm}
                    onChange={(e) => setRepairSearchTerm(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--dark-border)',
                      borderRadius: '20px',
                      backgroundColor: 'var(--dark-elevated)',
                      color: 'var(--text-primary)',
                      fontSize: '20px',
                      width: '250px'
                    }}
                  />
                </div>
                   {/* Add new wig-repair-option form */}
                   <div className="add-accessory-form">
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <label>Description</label>
                            <input 
                              type="text" 
                              value={newRepair.description}
                              onChange={(e) => setNewRepair({...newRepair, description: e.target.value})}
                            />
                          </td>
                          <td>
                            <label>Price</label>
                            <input 
                              type="number" 
                              step="0.01"
                              value={newRepair.price}
                              onChange={(e) => setNewRepair({...newRepair, price: e.target.value})}
                            />
                          </td>
                          <td>
                            <button 
                              type="button"
                              onClick={addCustomRepairToOrder}
                            >
                              Add to Order
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                <div className="pricing-table-container">
                  <table className="accessories-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Repair</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repairOptions.length > 0 ? (
                        (() => {
                          const typeOrder = ['Delivery', 'HandSew', 'Styling', 'Color', 'Wefts', 'Wig Repair', 'Machine'];

                          const filteredAndSorted = repairOptions
                            .filter(repair =>
                              repair.RepairDescription.toLowerCase().includes(repairSearchTerm.toLowerCase())
                            )
                            .sort((a, b) => {
                              const aIsWash = a.RepairDescription.toLowerCase().includes('wash');
                              const bIsWash = b.RepairDescription.toLowerCase().includes('wash');

                              if (aIsWash && !bIsWash) return -1;
                              if (!aIsWash && bIsWash) return 1;

                              const aTypeIndex = typeOrder.indexOf(a.Type);
                              const bTypeIndex = typeOrder.indexOf(b.Type);

                              if (aTypeIndex !== bTypeIndex) {
                                if (aTypeIndex === -1) return 1;
                                if (bTypeIndex === -1) return -1;
                                return aTypeIndex - bTypeIndex;
                              }

                              return a.RepairDescription.localeCompare(b.RepairDescription);
                            });

                          let lastGroup = null;
                          const rows = [];

                          filteredAndSorted.forEach((repair, index) => {
                            const isWash = repair.RepairDescription.toLowerCase().includes('wash');
                            const currentGroup = isWash ? 'Wash & Set' : repair.Type;

                            if (currentGroup !== lastGroup) {
                              rows.push(
                                <tr key={`header-${currentGroup}`} className="repair-type-header">
                                  <td colSpan="3" style={{
                                    backgroundColor: 'var(--dark-bg)',
                                    color: 'var(--text-primary)',
                                    fontWeight: 'bold',
                                    padding: '8px',
                                    borderTop: '2px solid #ccc'
                                  }}>
                                    {currentGroup}
                                  </td>
                                </tr>
                              );
                              lastGroup = currentGroup;
                            }

                            rows.push(
                              <tr key={index}>
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => addInventoryItem({
                                      style: repair.RepairDescription,
                                      price: `$${repair.RepairCost.toFixed(2)}`,
                                      type: 'repair'
                                    })}
                                  >
                                    Add
                                  </button>
                                </td>
                                <td>{repair.RepairDescription}</td>
                                <td>${repair.RepairCost.toFixed(2)}</td>
                              </tr>
                            );
                          });

                          return rows;
                        })()
                      ) : (
                        <tr>
                          <td colSpan="3">Loading repair options...</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  
                  
                  
               
                </div>
              
              </div>
              
              <div className="pricing-column">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>Available Accessories</h3>
                  <input
                    type="text"
                    placeholder="Search accessories..."
                    value={accessorySearchTerm}
                    onChange={(e) => setAccessorySearchTerm(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--dark-border)',
                      borderRadius: '20px',
                      backgroundColor: 'var(--dark-elevated)',
                      color: 'var(--text-primary)',
                      fontSize: '20px',
                      width: '250px'
                    }}
                  />
                </div>
                <div className="category-filter">
                  {/* Add new accessory form */}
                  <div className="add-accessory-form">
                    <table>
                     <tbody>
                        <tr>
                          <td>
                            <label>Category</label>
                            <select
                              value={newAccessory.category}
                              onChange={(e) => setNewAccessory({...newAccessory, category: e.target.value})}
                            >
                              <option value="">Select Category</option>
                              <option value="Accessories">Accessories</option>
                              <option value="Wig Heads">Wig Heads</option>
                              <option value="Wig Boxes">Wig Boxes</option>
                              <option value="Teeny Dini">Teeny Dini</option>
                              <option value="Bands">Bands</option>
                            </select>
                          </td>
                          <td>
                            <label>Description</label>
                            <input 
                              type="text"
                              value={newAccessory.description}
                              onChange={(e) => setNewAccessory({...newAccessory, description: e.target.value})}
                            />
                          </td>
                          <td>
                            <label>Price</label>
                            <input 
                              type="number" 
                              step="0.01"
                              value={newAccessory.price}
                              onChange={(e) => setNewAccessory({...newAccessory, price: e.target.value})}
                            />
                          </td>
                          <td>
                            <button 
                              type="button"
                              onClick={addCustomAccessoryToOrder}
                            >
                              Add to Order
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <label>Filter By Category:</label>
                  <select 
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Accessories">Accessories</option>
                    <option value="Wig Heads">Wig Heads</option>
                    <option value="Wig Boxes">Wig Boxes</option>
                    <option value="Teeny Dini">Teeny Dini</option>
                    <option value="Bands">Bands</option>
                  </select>
                </div>
                
                <div className="accessories-table-container">
                  
                  <table className="accessories-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Price</th>
                        {/* <th>Edit/Delete</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAccessories.length > 0 ? (
                        filteredAccessories
                         .filter(accessory => 
                            accessory.Accessory_Description.toLowerCase().includes(accessorySearchTerm.toLowerCase()) ||
                            accessory.Category.toLowerCase().includes(accessorySearchTerm.toLowerCase())
                          )
                          .map((accessory, index) => (
                          <tr key={index}>
                            <td>
                              <button 
                                type="button" 
                                onClick={() => addAccessory({
                                  category: accessory.Category,
                                  description: accessory.Accessory_Description,
                                  price: `$${accessory.Retail_Price.toFixed(2)}`
                                })}
                              >
                                Add
                              </button>
                            </td>
                            <td>{accessory.Category}</td>
                            <td>{accessory.Accessory_Description}</td>
                            <td>${accessory.Retail_Price.toFixed(2)}</td>
                            {/* <td>
                              <button type="button">Edit</button>
                              <button type="button">Delete</button>
                            </td> */}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5">Loading accessories...</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  
            
                </div>
              </div>
            </div>
          </div>
        )}

   

       

     {/* Order Items Section */}
     <div className="selected-items-section">
          <h3>Order Items</h3>
          
          {(selectedItems.length > 0 || selectedAccessories.length > 0) ? (
            <div className="items-summary">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Subtotal</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                {selectedItems.map(item => (
                  <tr key={item.id}>
                    <td>{item.type === 'repair' ? 'Wig Repair' : item.type === 'inventory' ? 'Wig' : 'Accessory'}</td>
                    <td>
                      {item.description}
                      {item.details && (
                        <div className="item-details">
                          <small>
                            {item.details.baseColor && `Color: ${item.details.baseColor}, `}
                            {item.details.inventoryType && `Type: ${item.details.inventoryType}, `}
                            {item.details.netSize && `Size: ${item.details.netSize}`}
                          </small>
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value))}
                        min="1"
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.price === 0 ? '' : item.price}
                        onChange={(e) => updateItemPrice(item.id, e.target.value)}
                        placeholder="0.00"   
                        style={{ width: '80px' }}
                      />
                    </td>
                    <td>${(item.price * item.quantity).toFixed(2)}</td>
                    <td>
                      <button
                        type="button"
                        className="remove-button"
                        onClick={() => removeInventoryItem(item.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>

                  ))}
                  {selectedAccessories.map(accessory => (
                    <tr key={accessory.id}>
                      <td>Accessory</td>
                      <td>{accessory.description}</td>
                      <td>
                        <input
                          type="number"
                          value={accessory.quantity}
                          onChange={(e) => updateAccessoryQuantity(accessory.id, parseInt(e.target.value))}
                          min="1"
                          style={{ width: '60px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={accessory.price === 0 ? '' : accessory.price}
                          onChange={(e) => updateAccessoryPrice(accessory.id, e.target.value)}
                          placeholder="0.00"
                          style={{ width: '80px' }}
                        />
                      </td>
                      <td>${(accessory.price * accessory.quantity).toFixed(2)}</td>
                      <td>
                        <button
                          type="button"
                          className="remove-button"
                          onClick={() => removeAccessory(accessory.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="total-summary">
                <strong>Order Total: ${calculateTotal().toFixed(2)}</strong>
              </div>
            </div>
          ) : (
            <p>No items added to order yet.</p>
          )}
        </div>

      </div>
        {/* Navigation Buttons */}
        <div className="button-group">
          <button 
          type="button" 
          className="back-btn" 
          onClick={handlePreviousClick}
          >
            Back
          </button>
          <button 
            type="button" 
            className="submit-btn" 
            onClick={() => {
              const dataToPass = {
                wigData: wigData,
                selectedItems: selectedItems,
                selectedAccessories: selectedAccessories,
                barcode: wigData.barcode,
                serialCode: wigData.serialCode,
                barcodeSearchResult: barcodeSearchResult,
                pricingData: pricingData,
                showPricing: showPricing,
                repairOptions: repairOptions,
                accessoryOptions: accessoryOptions,
                filteredAccessories: filteredAccessories,
                selectedCategory: selectedCategory
              };
              console.log("WigDetails passing data:", dataToPass);
              onNext(dataToPass);
            }}
            disabled={!canProceedToNext}
          >
           {!wigRequirementMet ?
              (normalizeOrderType(customerInfo?.orderType) === 'New Wig' ? 'Add Wig to Sell First' :
              customerInfo?.orderType === 'Accessory' || customerInfo?.orderType === 'Consultation' ? 'Next - Wig Rooms' :
              'Add Wig to Work On First') :
              repairGate.blocked ? (repairGate.pending ? 'Checking Warranty...' : 'Add Repair Cost First') :
              'Next - Wig Rooms'}
          </button>
          {getWigRequirementText() && (
            <div className="wig-requirement-notice" style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              color: '#856404',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <span>{getWigRequirementText()}</span>
              <br />
              <small>
                {normalizeOrderType(customerInfo?.orderType) === 'New Wig' ?
                  'Click "Sell wig" button above to add the wig to your order.' :
                  'Click "Work on this wig" button above to add the wig to your order.'}
              </small>
            </div>
          )}
          {wigRequirementMet && repairGate.blocked && !repairGate.pending && (
            <div className="wig-requirement-notice" style={{
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c2c7',
              color: '#842029',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <span>{repairGate.message}</span>
            </div>
          )}
          {isWigRepair && !repairGate.blocked && underWarranty && (
            <div className="wig-requirement-notice" style={{
              backgroundColor: '#d1e7dd',
              border: '1px solid #badbcc',
              color: '#0f5132',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <span>
                Covered under warranty{warrantyExpiresLabel ? ` (expires ${warrantyExpiresLabel})` : ''}.
              </span>
            </div>
          )}
        </div>
     
      
    </div>
  );
};

export default WigDetails;
